import { useCallback, useEffect, useState } from 'react'
import { isMobile } from 'react-device-detect'

const SW_URL = '/notifications-sw.js'
const ICON_URL = '/notification-icon.png'
const SW_READY_TIMEOUT_MS = 3000

// Solo en computador. En celulares (iPhone y Android) el sistema congela o cierra la web en
// segundo plano y, sin un servidor que envíe push, los avisos no llegan: no se ofrecen.
// En celulares queda la campana, que muestra el historial dentro de la página.
export const isNotificationSupported = () =>
  !isMobile &&
  typeof window !== 'undefined' &&
  window.isSecureContext &&
  'Notification' in window &&
  'serviceWorker' in navigator

const currentPermission = () =>
  isNotificationSupported() ? Notification.permission : 'unsupported'

// `ready` espera a que el service worker esté activo. Si no se pudo registrar
// (por ejemplo, certificado no confiable), nunca resuelve: por eso el tiempo límite.
const getActiveRegistration = () =>
  Promise.race([
    navigator.serviceWorker.ready,
    new Promise((resolve) => {
      setTimeout(() => resolve(null), SW_READY_TIMEOUT_MS)
    }),
  ])

const useBrowserNotifications = () => {
  const [permission, setPermission] = useState(currentPermission)

  useEffect(() => {
    if (isMobile) {
      // Quien ya lo había activado en el celular: se quita el service worker de avisos.
      navigator.serviceWorker?.getRegistrations?.()
        .then((registrations) => registrations
          .filter((registration) => registration.active?.scriptURL.endsWith(SW_URL))
          .forEach((registration) => registration.unregister()))
        .catch(() => {})
      return undefined
    }
    if (!isNotificationSupported()) return undefined

    navigator.serviceWorker.register(SW_URL).catch((error) => {
      console.error('[Notificaciones] Error registrando service worker:', error)
    })

    let permissionStatus = null
    const handlePermissionChange = () => setPermission(currentPermission())
    navigator.permissions
      ?.query({ name: 'notifications' })
      .then((status) => {
        permissionStatus = status
        status.addEventListener('change', handlePermissionChange)
      })
      .catch(() => {})

    return () => {
      permissionStatus?.removeEventListener('change', handlePermissionChange)
    }
  }, [])

  // Debe llamarse desde un toque del usuario: Chrome bloquea la petición si no.
  const requestPermission = useCallback(async () => {
    if (!isNotificationSupported()) return 'unsupported'
    const result = await Notification.requestPermission()
    setPermission(result)
    return result
  }, [])

  // Devuelve true si Chrome aceptó la notificación. El sistema operativo aún puede ocultarla.
  const notify = useCallback(async ({ title, body, tag }) => {
    if (currentPermission() !== 'granted') return false

    const options = {
      body,
      tag,
      icon: ICON_URL,
      renotify: Boolean(tag),
      data: { url: window.location.pathname },
    }

    try {
      // En Chrome Android `new Notification()` lanza error: hay que usar el service worker.
      const registration = await getActiveRegistration()
      if (registration) {
        await registration.showNotification(title, options)
        return true
      }
      // eslint-disable-next-line no-new
      new Notification(title, options)
      return true
    } catch (error) {
      console.error('[Notificaciones] No se pudo mostrar:', error)
      return false
    }
  }, [])

  return { permission, requestPermission, notify }
}

export { useBrowserNotifications }
